import { civilFromDays } from './civil.ts'
import { type LocalDate, localDate } from './local-date.ts'

export class InstantFormatError extends Error {
  readonly code = 'domain.instant_format'
  readonly value: string
  constructor(value: string) {
    super(`"${value}" is not an RFC 3339 UTC instant`)
    this.name = 'InstantFormatError'
    this.value = value
  }
}

declare const InstantBrand: unique symbol

/** A UTC point in time, lossless to nanosecond precision. */
export interface Instant {
  readonly epochSecond: bigint
  readonly nano: number
  readonly [InstantBrand]: true
}

const RFC_3339_UTC = /^(-?\d{4,})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z$/

const SECONDS_PER_DAY = 86_400n

export function instantOf(epochSecond: bigint, nano = 0): Instant {
  if (!Number.isInteger(nano) || nano < 0 || nano > 999_999_999) {
    throw new InstantFormatError(`nano=${nano}`)
  }
  return { epochSecond, nano } as Instant
}

export function parseInstant(value: string): Instant {
  const match = RFC_3339_UTC.exec(value)
  if (!match) throw new InstantFormatError(value)
  const [, year, month, day, hour, minute, second, fraction] = match
  const hours = Number(hour)
  const minutes = Number(minute)
  const seconds = Number(second)
  if (hours > 23 || minutes > 59 || seconds > 59) throw new InstantFormatError(value)
  const date = localDate(Number(year), Number(month), Number(day))
  const epochSecond = BigInt(date.epochDay) * SECONDS_PER_DAY + BigInt(hours * 3600 + minutes * 60 + seconds)
  return instantOf(epochSecond, Number((fraction ?? '').padEnd(9, '0') || '0'))
}

export function formatInstant(value: Instant): string {
  const day = Number(floorDiv(value.epochSecond, SECONDS_PER_DAY))
  const secondOfDay = Number(floorMod(value.epochSecond, SECONDS_PER_DAY))
  const { year, month, day: date } = civilFromDays(day)
  const hh = Math.floor(secondOfDay / 3600)
  const mm = Math.floor((secondOfDay % 3600) / 60)
  const ss = secondOfDay % 60
  const sign = year < 0 ? '-' : ''
  const stamp =
    `${sign}${String(Math.abs(year)).padStart(4, '0')}-${pad(month)}-${pad(date)}` +
    `T${pad(hh)}:${pad(mm)}:${pad(ss)}`
  return `${stamp}${formatFraction(value.nano)}Z`
}

/** Entry dates bucket in UTC server-side; rendering in another zone is off by one day. */
export function toLocalDateUtc(value: Instant): LocalDate {
  return { epochDay: Number(floorDiv(value.epochSecond, SECONDS_PER_DAY)) } as LocalDate
}

/** A date's own midnight UTC - for a wire field typed `Instant`, not the lenient `date`-only one entries use. */
export function startOfDayUtc(value: LocalDate): Instant {
  return instantOf(BigInt(value.epochDay) * SECONDS_PER_DAY)
}

export function plusSeconds(value: Instant, seconds: number): Instant {
  if (!Number.isInteger(seconds)) throw new InstantFormatError(`seconds=${seconds}`)
  return instantOf(value.epochSecond + BigInt(seconds), value.nano)
}

export function secondsBetween(from: Instant, until: Instant): bigint {
  return until.epochSecond - from.epochSecond
}

export function compareInstant(a: Instant, b: Instant): -1 | 0 | 1 {
  if (a.epochSecond !== b.epochSecond) return a.epochSecond < b.epochSecond ? -1 : 1
  return a.nano < b.nano ? -1 : a.nano > b.nano ? 1 : 0
}

export function equalsInstant(a: Instant, b: Instant): boolean {
  return a.epochSecond === b.epochSecond && a.nano === b.nano
}

export function toEpochMilli(value: Instant): number {
  return Number(value.epochSecond) * 1000 + Math.floor(value.nano / 1_000_000)
}

export function fromEpochMilli(epochMilli: number): Instant {
  const millis = BigInt(Math.trunc(epochMilli))
  return instantOf(floorDiv(millis, 1000n), Number(floorMod(millis, 1000n)) * 1_000_000)
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function formatFraction(nano: number): string {
  if (nano === 0) return ''
  const digits = String(nano).padStart(9, '0')
  if (digits.endsWith('000000')) return `.${digits.slice(0, 3)}`
  if (digits.endsWith('000')) return `.${digits.slice(0, 6)}`
  return `.${digits}`
}

function floorDiv(a: bigint, b: bigint): bigint {
  const q = a / b
  return a % b !== 0n && a < 0n !== b < 0n ? q - 1n : q
}

function floorMod(a: bigint, b: bigint): bigint {
  return a - floorDiv(a, b) * b
}
